import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { PropagationModal } from './propagation-modal';
import { EntityType } from '../../../../shared/enums/entity-type';
import { CloneToSwPartNumbers } from '../../../../shared/models/sw-part-number/clone-to-sw-part-numbers';
import { ErrorMessageApiService } from '../../../services/api/error-message-api.service';
import { SwPartNumberApiService } from '../../../services/api/sw-part-number-api.service';
import { PropagationTargetOption } from '../../../../shared/models/sw-part-number/propagation-target-option';
import { AgentActionDirective } from '../../../directives/agent-action.directive';

@Component({
  selector: 'app-propagate-created-entity-modal',
  imports: [AgentActionDirective],
  templateUrl: './propagation-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './propagation-modal.scss',
})
export class PropagateCreatedEntityModal extends PropagationModal {
  private readonly swPartNumberApi = inject(SwPartNumberApiService);

  private readonly errorMessageApi = inject(ErrorMessageApiService);

  private swPartNumberId?: string;

  private entityType?: EntityType;

  public override openModal(swPartNumberId?: string, entityType?: EntityType, entityId?: string): void {
    this.swPartNumberId = swPartNumberId;
    this.entityType = entityType;
    this.entityId = entityId;
    this.businessObject = entityType;
    this.propagationTargetOptions = [];
    this.swPartNumberApi.getDevSwPartNumbersWithSameNwNumber(swPartNumberId!).subscribe({
      next: (devSwPartNumberCollection) => {
        devSwPartNumberCollection.devSwPartNumbersWithSameNwNumber.forEach((swPartNumberInfo) => {
          const swPartNumberElement: PropagationTargetOption = {
            id: swPartNumberInfo.devSwPartNumberId,
            number: swPartNumberInfo.number,
            shouldCreate: swPartNumberInfo.shouldCreate,
            readOnly: swPartNumberInfo.readOnly,
            isVisible: true,
          };
          this.propagationTargetOptions?.push(swPartNumberElement);
          if (swPartNumberInfo.devSwPartNumberId !== swPartNumberId && swPartNumberInfo.shouldCreate) {
            this.selectedOptionIds.push(swPartNumberInfo.devSwPartNumberId);
          }
        });
        if (this.isOnlyEditedEntity) {
          this.closeModal();
        } else {
          super.openModal();
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  public override closeModal(): void {
    this.swPartNumberId = undefined;
    this.entityType = undefined;
    this.entityId = undefined;
    this.businessObject = undefined;
    this.propagationTargetOptions = undefined;
    this.selectedOptionIds = [];
    super.closeModal();
  }

  protected override execute(): void {
    if (!this.swPartNumberId || !this.entityType || !this.entityId) {
      console.error('Missing required information to create in other majors');
      return;
    }
    const cloneToSwPartNumbers: CloneToSwPartNumbers = {
      sourceEntityId: this.entityId,
      swPartNumberIdsToCreate: this.selectedOptionIds,
    };
    if (this.entityType === EntityType.Dtc) {
      this.cloneDtcToSwPartNumbers(cloneToSwPartNumbers);
    } else if (this.entityType === EntityType.ErrorMessage) {
      this.cloneErrorMessageToSwPartNumbers(cloneToSwPartNumbers);
    }
  }

  private cloneDtcToSwPartNumbers(cloneToSwPartNumbers: CloneToSwPartNumbers): void {
    this.dtcApi.cloneDtcToSwPartNumbers(cloneToSwPartNumbers).subscribe({
      next: () => {
        this.toast.success('DTC successfully cloned to selected SW Part Numbers');
        this.closeModal();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  private cloneErrorMessageToSwPartNumbers(cloneToSwPartNumbers: CloneToSwPartNumbers): void {
    this.errorMessageApi.cloneErrorMessageToSwPartNumbers(cloneToSwPartNumbers).subscribe({
      next: () => {
        this.toast.success('Error Message successfully cloned to selected SW Part Numbers');
        this.closeModal();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  private get isOnlyEditedEntity(): boolean {
    return (
      this.propagationTargetOptions?.length === 1 &&
      this.propagationTargetOptions[0].id === this.swPartNumberId &&
      this.propagationTargetOptions[0].readOnly
    );
  }
}
